import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Search, ShoppingCart, ArrowRight } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { interpretQuickOrder } from '../../utils/quickOrder';

/**
 * Dashboard entry point for the two most common jobs: find a part, or order a
 * list of parts. One line is a part search; several lines (or "part, qty") go
 * to the Bulk Upload review screen, which already handles stock, pricing,
 * alternatives and merging into the cart.
 */
const QuickOrderBox = ({ userId }) => {
  const history = useHistory();
  const [text, setText] = useState('');

  const intent = interpretQuickOrder(text);

  const submit = async () => {
    if (intent.type === 'search') {
      if (userId) {
        const { error } = await supabase
          .from('search_history')
          .insert({ user_id: userId, part_number: intent.term, search_type: 'exact' });
        if (error) console.error('Error adding search to history:', error);
      }
      // Same route as the header search: exact match first, Part Enquiry falls
      // back to smart search when there is no exact hit.
      history.push(`/user/part-enquiry?partNumber=${encodeURIComponent(intent.term)}`);
    } else if (intent.type === 'order') {
      history.push('/user/bulk-upload', {
        quickOrderLines: intent.lines.map((l) => ({ PartNumber: l.partNumber, Quantity: l.quantity })),
      });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const multiLine = text.includes('\n');

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
      <label htmlFor="quick-order" className="block text-base font-semibold text-gray-900">
        Find or order parts
      </label>
      <p className="text-sm text-gray-500 mt-0.5">
        Type a part number to search, or paste a list with quantities (one per line, e.g. from Excel).
      </p>
      <div className="mt-3 flex flex-col sm:flex-row gap-3 sm:items-start">
        <textarea
          id="quick-order"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={multiLine ? Math.min(8, text.split('\n').length + 1) : 1}
          placeholder="e.g. SP 212 PP  —  or paste:  ABC123, 10"
          spellCheck={false}
          autoComplete="off"
          className="flex-1 resize-none px-3 py-2.5 border border-gray-300 rounded-lg font-mono text-sm text-gray-900 placeholder:font-sans placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
        />
        <button
          type="button"
          onClick={submit}
          disabled={intent.type === 'empty'}
          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-brand text-brand-dark rounded-lg font-semibold hover:bg-brand-hover transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
        >
          {intent.type === 'order' ? (
            <>
              <ShoppingCart className="w-4 h-4" aria-hidden="true" />
              Review {intent.lines.length} {intent.lines.length === 1 ? 'line' : 'lines'}
            </>
          ) : (
            <>
              <Search className="w-4 h-4" aria-hidden="true" />
              Search
            </>
          )}
          <ArrowRight className="w-4 h-4" aria-hidden="true" />
        </button>
      </div>
      <p className="text-xs text-gray-500 mt-2">
        Enter to go · Shift+Enter for a new line · separate quantity with a comma or tab
      </p>
    </div>
  );
};

export default QuickOrderBox;
